/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, Input } from 'reactstrap';
import Alert from 'react-s-alert';
import { GoogleLogin } from 'react-google-login';
import moment from 'moment';

import config from '../../config.json';

// context imports
import { AuthContext } from '../../contexts/AuthContext'
import { ThemeContext } from '../../contexts/ThemeContext';

// service imports
import GeneralService from '../../services/GeneralService';
import AuthService from '../../services/AuthService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';
import Message from '../general/Message';
import GeneralNavbar from '../general/GeneralNavbar';
import GoogleLogo from '../../assets/GoogleLogo.svg';
import './SignIn.css';

const SignIn = props => {

    // contexts
    const authContext = useContext(AuthContext);
    const { theme, colors, fontFamily, fontSize, fontWeight } = useContext(ThemeContext);

    // states
    const [user, setUser] = useState({ email: "", password: "", isTwoFactor: false, otp: "", rememberMe: false });
    const [gotoForm, setGotoForm] = useState({ status: false, url: "" });
    const [message, setMessage] = useState(null);
    const [modal, setModal] = useState({ isVisible: false, email: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [showResend, setShowResend] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [isGoogleLogin, setIsGoogleLogin] = useState(false);
    const [agreeBox, setAgreeBox] = useState(false);
    const [dateBeforeYears, setDateBeforeYears] = useState(moment());
    const [minimumAge, setMinimumAge] = useState(0);
    const [ageBox, setAgeBox] = useState(false);
    const [googleRegisterModal, setGoogleRegisterModal] = useState({ visible: false, renderProps: null });
    const [redirectTo, setRedirectTo] = useState('');
    let timerID = useRef(null);

    useEffect(() => {
        try {
            let params = new URLSearchParams(props.location.search)
            setRedirectTo(params.get('redirect'));
            document.getElementById("email").focus();
            if (props.location.state) {
                if(props.location.state.from && props.location.state.from.pathname === "/counsellor/landingScreen") {
                    props.history.replace("/user/signup");
                } else if(props.location.state.from) {
                    setRedirectTo(props.location.state.from.pathname);
                }else if (props.location.state.from && props.location.state.from.pathname === "/client/form/clientForm") {
                    setGotoForm({ status: true, url: "/client/form/clientForm" });
                } else if (props.location.state.from && props.location.state.from.pathname === "/client/form/teenForm") {
                    setGotoForm({ status: true, url: "/client/form/teenForm" });
                } else if (props.location.state.from && props.location.state.from.pathname === "/client/form/adultForm") {
                    setGotoForm({ status: true, url: "/client/form/adultForm" });
                } else if (props.location.state.from && props.location.state.from.pathname === "/client/form/coupleForm") {
                    setGotoForm({ status: true, url: "/client/form/coupleForm" });
                } else {
                    setMessage(null);
                }
                if (props.location.state.email) {
                    setUser({ ...user, email: props.location.state.email });
                    document.getElementById("password").focus();
                }
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at User SignIn useEffectUp', 'Error in try catch block', err.stack);
        }
        GeneralService.getMinimumAge().then(data => {
            try {
                setDateBeforeYears(dateBeforeYears.subtract(data.minimumAge, 'years'));
                setMinimumAge(data.minimumAge);
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at User SignIn useEffect', 'Error in try catch block', err.stack);
            }
        });
        return () => {
            clearTimeout(timerID)
        }
    }, [])

    // Utility functions
    // reset form 
    const resetForm = () => {
        // setUser({username: "", email: "", password: "", confirmPassword: ""});
    }

    // handle functions
    const handleChange = e => {
        try {
            setUser({ ...user, [e.target.name]: e.target.value });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at User SignIn handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handleResendVerification = e => {
        e.preventDefault();
        try {
            setIsLoading(true);
            AuthService.sendVerifyMail(modal.email)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading(false);
                    setModal({ isVisible: false, email: '' });
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at User SignIn handleResendVerification', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading2(true);
            if (user.isTwoFactor) {
                AuthService.signin(user)
                    .then(data => {
                        setIsLoading2(false);
                        const { isAuthenticated, user, message } = data;
                        if (isAuthenticated) {
                            setMessage(message);
                            authContext.setUser({ ...user, selectedRole: user.roles.length ? user.roles[0] : '' });
                            authContext.setIsAuthenticated(isAuthenticated);

                            if (redirectTo) {
                                props.history.push(redirectTo);
                            } else if (user.selectedRole === '') {
                                props.history.push('/user/landingScreen');
                            } else if (user.selectedRole === "admin") {
                                props.history.push('/admin/dashboard');
                            } else if (authContext.maintenance.status) {
                                AuthService.logout().then(() => {
                                    props.history.replace('/maintenance');
                                })
                            } else if (user.selectedRole === "client") {
                                if (gotoForm.status) {
                                    props.history.push(gotoForm.url)
                                } else {
                                    props.history.push('/client/dashboard');
                                }
                            } else if (user.selectedRole === "provider") {
                                props.history.push('/counsellor/sessions');
                            }
                        }
                    })
                    .catch(error => {
                        if (error.emailVerified !== undefined && error.emailVerified === false) {
                            setShowResend(true);
                        } else {
                            setShowResend(false);
                        }
                        if (error.userExists !== undefined && error.userExists === false) {
                            props.history.push({ pathname: '/user/signup', state: { email: user.email, message: { msgError: false, msgBody: 'User does not exists, please signup to continue' } } });
                        }
                        setMessage(error.message);
                        setIsLoading2(false);
                    });
            } else {
                AuthService.signin(user)
                    .then(data => {
                        setIsLoading2(false);
                        const { isTwoFactorOn, message } = data;
                        if (isTwoFactorOn) {
                            setMessage(message);
                            setUser({ ...user, isTwoFactor: true });
                            setShowOtp(true);
                            document.getElementById("otpInput").focus();
                        } else {
                            const { isAuthenticated, user, message } = data;
                            if (isAuthenticated) {
                                setMessage(message);
                                authContext.setUser({ ...user, selectedRole: user.roles.length ? user.roles[0] : '' });
                                authContext.setIsAuthenticated(isAuthenticated);

                                if (redirectTo) {
                                    props.history.push(redirectTo);
                                } else if (user.selectedRole === '') {
                                    props.history.push('/user/landingScreen');
                                } else if (user.selectedRole === "admin") {
                                    props.history.push('/admin/dashboard');
                                } else if (authContext.maintenance.status) {
                                    AuthService.logout().then(() => {
                                        props.history.replace('/maintenance');
                                    })
                                } else if (user.selectedRole === "client") {
                                    if (gotoForm.status) {
                                        props.history.push(gotoForm.url)
                                    } else {
                                        props.history.push('/client/dashboard');
                                    }
                                } else if (user.selectedRole === "provider") {
                                    props.history.push('/counsellor/sessions');
                                }
                            }
                        }
                    })
                    .catch(error => {
                        console.log('error', error, error.userExists !== undefined, error.userExists === false);
                        if (error.emailVerified !== undefined && error.emailVerified === false) {
                            setShowResend(true);
                        } else {
                            setShowResend(false);
                        }
                        if (error.userExists !== undefined && error.userExists === false) {
                            props.history.push({ pathname: '/user/signup', state: { email: user.email, message: { msgError: false, msgBody: 'User does not exists, please signup to continue' } } });
                        }
                        setMessage(error.message);
                        setIsLoading2(false);
                    });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading2(false);
            GeneralService.logError('at User SignIn handleSubmit', 'Error in try catch block', err.stack);
        }
    }

    const googleResponse = (response) => {
        AuthService.loginUsingGoogle(response).then(data => {
            const { isAuthenticated, user, message, isGoogleLogin } = data;
            if (isAuthenticated) {
                setMessage(message);
                authContext.setUser({ ...user, selectedRole: user.roles.length ? user.roles[0] : '' });
                authContext.setIsAuthenticated(isAuthenticated);
                authContext.setIsGoogleLogin(isGoogleLogin);

                if (user.selectedRole === "admin") {
                    props.history.push('/admin/dashboard');
                } else if (authContext.maintenance.status) {
                    AuthService.logout().then(() => {
                        props.history.replace('/maintenance');
                    })
                } else if (user.selectedRole === "client") {
                    if (gotoForm.status) {
                        props.history.push(gotoForm.url)
                    } else {
                        props.history.push('/client/dashboard');
                    }
                }
                else if (user.selectedRole === "provider") {
                    props.history.push('/counsellor/sessions');
                }
            }
        }).catch(error => {
            // setMessage(error.message);
        });
    }

    const handleClickOnLink = () => {
        // props.history.push("/user/signup")
        try {
            props.history.push({
                pathname: "/user/signup"
            })
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
        }


    }

    // function's return
    return (
        <div>
            <ErrorBoundary errorAt="At User SignIn GeneralNavbar">
                <GeneralNavbar history={props.history} isSignInPage={true} />
            </ErrorBoundary>
            <div className="p-3 col-md-4 mt-5 mb-5 offset-md-4" style={theme.form}>
                <form onSubmit={handleSubmit} style={{ paddingLeft: "10%", paddingRight: "10%" }}>
                    <h2 id="login-heading" style={theme.heading}>Sign In to your account</h2>
                    <br />

                    <ErrorBoundary errorAt="At User SignIn Message">
                        {message ? <Message message={message} /> : null}
                    </ErrorBoundary>

                    <ErrorBoundary errorAt="At User SignIn Fields">
                        <label id="form-email" htmlFor="email" style={theme.formBodyText} >Email </label>
                        <input type="text"
                            id="email"
                            name="email"
                            value={user.email}
                            onChange={handleChange}
                            className="form-control"
                            disabled={isGoogleLogin}
                            required
                            style={theme.formInput}
                        />
                        <br />

                        <label htmlFor="password" id="form-password" style={theme.formBodyText} >Password </label>
                        <input type="password"
                            id="password"
                            name="password"
                            value={user.password}
                            onChange={handleChange}
                            className="form-control"
                            required
                            style={theme.formInput}
                        />

                        {
                            showOtp ?
                                <div>
                                    <label htmlFor="otp" style={theme.formBodyText} >OTP </label>
                                    <input type="text"
                                        id="otpInput"
                                        name="otp"
                                        value={user.otp}
                                        onChange={handleChange}
                                        className="form-control"
                                        required
                                        style={theme.formInput}
                                    />
                                    <label style={{ fontSize: fontSize.sm }}>*Check your Spam/Junk email folder if you do not see the OTP in your email Inbox</label>
                                </div>
                                : null
                        }

                        <div className="d-flex mt-2">
                            <div className="form-check">
                                <input className="form-check-input" type="checkbox" style={{ width: "1rem", height: "1rem" }} checked={user.rememberMe} id="rm" onChange={(e) => setUser({ ...user, rememberMe: e.target.checked })} />
                                <label className="form-check-label" htmlFor="rm" id="form-remember">Remember me</label>
                            </div>
                            <div className="ml-auto">
                                <label style={{ fontFamily: fontFamily.primary, fontSize: "0.9rem", color: colors.primaryGreenDark }}>
                                    <Link to={`/user/forgotpassword${user.email ? "?email=" + user.email : ""}`} id="form-forgot-password">
                                        Forgot password?
                                    </Link>
                                </label>
                            </div>
                        </div>

                        {
                            showResend ?
                                <div className="d-flex mt-2">
                                    <div className="ml-auto">
                                        <label style={{ fontFamily: fontFamily.primary, fontSize: "0.9rem", color: colors.primaryGreenDark }}>
                                            <Link to="#" onClick={() => setModal({ isVisible: true, email: user.email })}>
                                                Didn't get verification email? Please click on this link to resend.
                                            </Link>
                                        </label>
                                    </div>
                                </div>
                                : null
                        }

                        <button className="btn mt-2" id="signin-btn" type="submit" style={theme.formSubmit} disabled={isLoading2}>{isLoading2 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Sign In</button>

                        <p className="my-1" style={{ fontSize: '1.2rem' }}>or</p>

                        <div className="my-2">
                            <GoogleLogin id="google-singin"
                                clientId={config.googleClientId}
                                render={renderProps => (
                                    <div className="google-btn" onClick={() => renderProps.onClick()}>
                                        <div className="google-icon-wrapper">
                                            <img className="google-icon" src={GoogleLogo} />
                                        </div>
                                        <p className="btn-text"><b> Sign In with Google</b></p>
                                    </div>
                                )}
                                cookiePolicy={'single_host_origin'}
                                onSuccess={(res) => googleResponse(res)}
                                onFailure={(err) => googleResponse(err)}
                            />
                        </div>

                        <p style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm }} id="signin-google-condition">*By clicking on "Sign In with Google" you indicate that you agree to our <Link to='/termsconditions'><u>Terms &#38; Conditions</u></Link>, <Link to='/privacypolicy'><u>Privacy Policy</u></Link> and <Link to='/cookiepolicy'><u>Cookie Policy</u></Link>.</p>

                        <p style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm, marginTop: "10px" }} >
                            Don't have an account?
                            {/* <Link to="/user/signup">Here</Link> */}
                            <span id="signin-link" onClick={handleClickOnLink} style={{ color: colors.primaryGreenDark, fontWeight: fontWeight.bold, cursor: 'pointer' }}> Sign Up here</span>
                        </p>
                    </ErrorBoundary>
                </form>
            </div>

            <ErrorBoundary errorAt="At User SignIn Modal">
                <Modal size="md" isOpen={modal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setModal({ isVisible: false, email: '' })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Resend verification email</span></ModalHeader>
                    <ModalBody>
                        <Label>Enter your email and click on button below to get verification email.</Label>
                        <Input type="text" id="reEmail" name="reEmail" value={modal.email} onChange={(e) => setModal({ ...modal, email: e.target.value })} />
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={handleResendVerification} disabled={isLoading}>
                            {isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Resend mail
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
        </div>
    )


}

export default SignIn;